import React, { useState, useEffect, useCallback } from 'react';
import { Flag, Loader2, RefreshCw, Eye, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

export default function AdminFeatureFlagsPage() {
  const { colors } = useAdminTheme();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({});

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/feature-flags`, { withCredentials: true });
      setFlags(res.data?.flags || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const toggle = async (key, enabled) => {
    setPending((p) => ({ ...p, [key]: true }));
    // Optimistic
    setFlags((prev) => prev.map((f) => f.key === key ? { ...f, enabled } : f));
    try {
      await axios.put(
        `${API}/admin/feature-flags/${key}`,
        { enabled },
        { withCredentials: true },
      );
      toast.success(`'${key}' is now ${enabled ? 'ON' : 'OFF'}`);
    } catch (err) {
      // Revert
      setFlags((prev) => prev.map((f) => f.key === key ? { ...f, enabled: !enabled } : f));
      toast.error(err?.response?.data?.detail || 'Failed to update flag');
    } finally {
      setPending((p) => ({ ...p, [key]: false }));
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl" data-testid="admin-feature-flags-page">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: colors.text }}>
              <Flag className="h-5 w-5" />
              Feature Flags
            </h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              Turn features on or off for end users without a redeploy.
              Admins always see flagged features in preview mode.
            </p>
          </div>
          <button
            onClick={fetchFlags}
            disabled={loading}
            className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all"
            style={{ background: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
            data-testid="admin-feature-flags-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Info notice — semantics are literal, no auto-preview */}
        <div
          className="mb-5 rounded-xl px-4 py-3 flex items-start gap-3 text-sm"
          style={{
            background: `${colors.accent}11`,
            border: `1px solid ${colors.accent}33`,
            color: colors.text,
          }}
        >
          <Eye className="h-4 w-4 mt-0.5 shrink-0" style={{ color: colors.accent }} />
          <div>
            <p className="font-medium">OFF means OFF — including for admins</p>
            <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
              Toggling a flag OFF hides that feature from every user, admins
              included. To preview a disabled feature yourself, flip the
              switch back ON.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
          </div>
        ) : flags.length === 0 ? (
          <div
            className="rounded-xl p-8 text-center text-sm"
            style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.textMuted }}
          >
            No feature flags registered yet.
          </div>
        ) : (
          <div className="space-y-8" data-testid="admin-feature-flags-list">
            {(() => {
              // Group by category, preserving the insertion order of the
              // first flag in each category (seed order → intentional
              // grouping like "Sharing", "Community", "Study Tools" ...).
              const groups = [];
              const map = new Map();
              for (const f of flags) {
                const cat = f.category || 'General';
                if (!map.has(cat)) {
                  map.set(cat, []);
                  groups.push(cat);
                }
                map.get(cat).push(f);
              }
              return groups.map((cat) => {
                const items = map.get(cat);
                const liveCount = items.filter((x) => x.enabled).length;
                return (
                  <section key={cat} data-testid={`flag-group-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                    <header className="mb-3 flex items-baseline justify-between">
                      <h2 className="text-[13px] uppercase tracking-widest font-bold"
                          style={{ color: colors.text }}>
                        {cat}
                      </h2>
                      <span className="text-[11px]" style={{ color: colors.textMuted }}>
                        {liveCount} of {items.length} live
                      </span>
                    </header>
                    <div className="space-y-3">
                      {items.map((f) => (
                        <div
                          key={f.key}
                          className="rounded-xl p-4 transition-all"
                          style={{
                            background: colors.surface,
                            border: `1px solid ${f.enabled ? colors.accent + '55' : colors.border}`,
                          }}
                          data-testid={`flag-row-${f.key}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="font-semibold text-sm" style={{ color: colors.text }}>
                                  {f.label || f.key}
                                </h3>
                                <code
                                  className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                                  style={{ background: colors.background, color: colors.textMuted }}
                                >
                                  {f.key}
                                </code>
                                <span
                                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                                  style={{
                                    background: f.enabled ? colors.accent + '22' : colors.background,
                                    color: f.enabled ? colors.accent : colors.textMuted,
                                  }}
                                >
                                  {f.enabled ? 'LIVE' : 'OFF'}
                                </span>
                              </div>
                              {f.description && (
                                <p className="text-xs leading-relaxed" style={{ color: colors.textMuted }}>
                                  {f.description}
                                </p>
                              )}
                              {f.depends_on && (
                                <p className="text-[11px] mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                                   style={{ background: colors.background, color: colors.textMuted }}>
                                  <span>requires</span>
                                  <code className="font-mono">{f.depends_on}</code>
                                </p>
                              )}
                              <p className="text-[11px] mt-2" style={{ color: colors.textMuted }}>
                                Last changed by <span className="font-medium">{f.updated_by || 'unknown'}</span> · {formatTime(f.updated_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0 pt-0.5">
                              {pending[f.key] ? (
                                <Loader2 className="h-4 w-4 animate-spin" style={{ color: colors.accent }} />
                              ) : null}
                              <Switch
                                checked={!!f.enabled}
                                onCheckedChange={(v) => toggle(f.key, v)}
                                disabled={!!pending[f.key]}
                                data-testid={`flag-toggle-${f.key}`}
                              />
                            </div>
                          </div>

                          {!f.enabled && (
                            <div
                              className="mt-3 flex items-start gap-2 text-[11px] leading-relaxed pt-3"
                              style={{ borderTop: `1px solid ${colors.border}`, color: colors.textMuted }}
                            >
                              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: '#f59e0b' }} />
                              <span>Currently hidden from end users. Toggle ON to make this feature live.</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              });
            })()}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
